import { Box, CircularProgress, Table, Typography } from '@mui/material';
import { IPrjectMemberGridColumns, IProjectMembersDataTest } from '../type';
import { ViewType } from '../../common/utils/enum';
import { TableVirtuoso } from 'react-virtuoso';
import MemberNameCol from './MemberNameCol';
import ProjectMemberAdminColumns from './project-member-admin-columns';

const statusLabelMap = {
  PENDING: 'Waiting for Approval',
  APPROVED: 'Active',
  REJECTED: 'Rejected'
};
const statusColorMap = {
  PENDING: '#EA9515',
  APPROVED: '#06BF3A',
  REJECTED: '#F3281C'
};
const cellTypoStyle = {
  fontFamily: 'Be Vietnam Pro',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '24px',
  letterSpacing: '0.5px',
  textAlign: 'left'
};

interface ITableData {
  updateProjectMemberCallback: () => Promise<void>;
  columns: IPrjectMemberGridColumns[];
  isLoading?: boolean;
  view?: ViewType;
  data: IProjectMembersDataTest[];
  canEdit?: boolean;
  canDelete?: boolean;
  projectId: string;
  isAdmin: boolean;
}

const ProjectDataTable = ({
  updateProjectMemberCallback,
  columns,
  isLoading,
  data,
  canEdit = false,
  canDelete = false,
  projectId,
  isAdmin
}: ITableData): JSX.Element => {
  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            width: '100%',
            height: '40vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none'
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableVirtuoso
            scrolling="smooth"
            components={{
              Table: (props) => (
                <Table
                  {...props}
                  style={{ width: '100%' }}
                  data-testid={'table'}
                />
              )
            }}
            data={data}
            fixedHeaderContent={() => (
              <tr style={{ background: '#FFFFFF' }}>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    style={{
                      width: column.width,
                      minWidth: column.minWidth,
                      padding: '5px',
                      textAlign: 'left',
                      borderBottom: '1px solid #F2F0F4',
                      backgroundColor: '#E5F2FF'
                    }}
                  >
                    {column.headerName}
                  </th>
                ))}
              </tr>
            )}
            itemContent={(index: number, item) => {
              return (
                <>
                  <td
                    style={{
                      height: '60px',
                      borderBottom: '1px solid #F2F0F4',
                      paddingLeft: '10px'
                    }}
                  >
                    {' '}
                    {(index + 1).toString()}{' '}
                  </td>
                  <td
                    style={{
                      height: '60px',
                      borderBottom: '1px solid #F2F0F4'
                    }}
                  >
                    <MemberNameCol
                      id={item.member?.id}
                      providerId={item.member?.providerId}
                      displayName={item.member?.displayName}
                    />
                  </td>
                  <td
                    style={{
                      height: '60px',
                      borderBottom: '1px solid #F2F0F4'
                    }}
                  >
                    {item.memberRoles?.map((role) => {
                      return (
                        <span key={role.projectRole.id}>
                          {role.projectRole.displayName}
                          <br></br>
                        </span>
                      );
                    })}
                  </td>
                  {((canEdit && canDelete) || isAdmin) && (
                    <td
                      style={{
                        height: '60px',
                        borderBottom: '1px solid #F2F0F4'
                      }}
                    >
                      {' '}
                      {item.hoursPerWeek}{' '}
                    </td>
                  )}
                  <td
                    style={{
                      height: '60px',
                      borderBottom: '1px solid #F2F0F4'
                    }}
                  >
                    {' '}
                    {item.assignedAt
                      ? new Date(item.assignedAt).toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric'
                        })
                      : ''}{' '}
                  </td>
                  {((canEdit && canDelete) || isAdmin) && (
                    <td
                      style={{
                        height: '60px',
                        borderBottom: '1px solid #F2F0F4',
                        width: '50px'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start'
                        }}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            display: 'inline-block',
                            backgroundColor:
                              statusColorMap[
                                item.memberStatus as keyof typeof statusColorMap
                              ] || 'grey',
                            marginRight: 1,
                            verticalAlign: 'middle'
                          }}
                        />
                        <Typography sx={cellTypoStyle}>
                          {statusLabelMap[
                            item.memberStatus as keyof typeof statusLabelMap
                          ] || item.memberStatus}
                        </Typography>
                      </Box>
                    </td>
                  )}
                  <td
                    style={{
                      height: '60px',
                      borderBottom: '1px solid #F2F0F4'
                    }}
                  >
                    {canEdit && canDelete && item && item.member.id && (
                      <ProjectMemberAdminColumns
                        rowItem={item}
                        updateProjectMemberCallback={
                          updateProjectMemberCallback
                        }
                        memberStatus={item.memberStatus ?? 'PENDING'}
                        projectId={projectId}
                        canEdit={true}
                        canDelete={
                          !item.memberRoles.some(
                            (role) =>
                              role.projectRole.role === 'DELIVERY_RESPONSIBLE'
                          )
                        }
                      />
                    )}
                  </td>
                </>
              );
            }}
          />
        </>
      )}
    </>
  );
};

export default ProjectDataTable;
